import type { ComboboxOption } from '@atlas/ui-primitives'
import type { CampaignFormFieldErrors, CampaignFormValues } from '@/components/campaigns/CampaignForm'
import type { Campaign, CreateCampaignInput, UpdateCampaignInput } from '@/domain/campaign.types'
import type { CampaignClassificationVariable } from '@/domain/campaign-classification-catalog'

// The Campaign form model, shared by both New and Edit: initial values,
// validation, and the partial-update diff builder. `campaignUpdateSchema`'s
// `.nullable().optional()` split on `description`/`windowLabel` is mirrored
// exactly — an untouched field is omitted, an explicitly cleared one emits
// `null`. `veevaCode` is not nullable on the real update contract (clearing a
// Veeva code is out of scope), so a cleared, touched Veeva code is omitted
// rather than sent as `null`.

/** Map a Classification variable's options to the shape `CampaignForm`'s Comboboxes render.
 *  Order is preserved, not re-sorted: the catalog adapter already emits each axis
 *  parent-before-child, and sorting here would flatten that back into a flat list. */
export function toComboboxOptions(variable: CampaignClassificationVariable | undefined): ComboboxOption[] {
	return (variable?.options ?? []).map((option) => ({ value: option.code, label: option.label }))
}

export const EMPTY_CAMPAIGN_FORM_VALUES: CampaignFormValues = {
	name: '',
	windowLabel: '',
	description: '',
	veevaCode: '',
	objectiveCode: null,
	geographyCode: null,
	brandCode: null,
	productCode: null,
}

export type CampaignFormTouched = Partial<Record<keyof CampaignFormValues, boolean>>

/**
 * Seed form values from an existing Campaign, for Edit.
 *
 * `productCodes` says which values of the brand axis are PRODUCTS. Brand and product are
 * two levels of one axis and the write collapses to the most specific value, so a single
 * stored code has to fill the right one of two fields — and only the catalog knows which
 * level it names. Codes are prefixed by convention (`br_`/`prod_`), but the convention is
 * not enforced by the database, so sniffing the string would silently put a product in
 * the Brand field for every value that predates it.
 *
 * Omitted (or empty) means the catalog has not loaded: the code then fills Brand, which
 * shows the reader the value that IS stored rather than blanking both fields.
 */
export function campaignFormValuesFrom(
	campaign: Campaign,
	productCodes: ReadonlySet<string> = new Set(),
): CampaignFormValues {
	return {
		name: campaign.name,
		windowLabel: campaign.windowLabel ?? '',
		description: campaign.description ?? '',
		veevaCode: campaign.veevaCode ?? '',
		objectiveCode: campaign.classification.selection.objective?.[0] ?? null,
		geographyCode: campaign.classification.selection.geography?.[0] ?? null,
		// Reading a `product` key would always find nothing: nothing writes one.
		brandCode: brandLevelOf(campaign.classification.selection.brand?.[0] ?? null, 'brand', productCodes),
		productCode: brandLevelOf(campaign.classification.selection.brand?.[0] ?? null, 'product', productCodes),
	}
}

/** Which level of the brand axis a stored code names, per the catalog. */
function brandLevelOf(
	code: string | null,
	want: 'brand' | 'product',
	productCodes: ReadonlySet<string>,
): string | null {
	if (!code) return null
	return (want === 'product') === productCodes.has(code) ? code : null
}

/** Name is required; a supplied Veeva code must be non-empty once trimmed. */
export function validateCampaignForm(values: CampaignFormValues): CampaignFormFieldErrors {
	const errors: CampaignFormFieldErrors = {}
	if (values.name.trim().length === 0) errors.name = 'Name is required.'
	if (values.veevaCode.length > 0 && values.veevaCode.trim().length === 0) {
		errors.veevaCode = 'Veeva code cannot be only whitespace.'
	}
	return errors
}

function classificationSelectionFrom(values: CampaignFormValues): Record<string, string[]> {
	const selection: Record<string, string[]> = {}
	if (values.objectiveCode) selection.objective = [values.objectiveCode]
	if (values.geographyCode) selection.geography = [values.geographyCode]
	// Brand and product are two levels of the `brand` axis, and they collapse to the MOST
	// SPECIFIC value: a product is a descendant of its brand, so tagging the product already
	// implies the brand through the closure. Writing `selection.product` instead named an
	// axis that does not exist — `classify` throws on one it cannot resolve, and the service
	// then compensates by deleting the campaign it had just created.
	const mostSpecificBrand = values.productCode || values.brandCode
	if (mostSpecificBrand) selection.brand = [mostSpecificBrand]
	return selection
}

/** Build the create payload: empty optional fields are omitted, never a supplied empty string. */
export function buildCreateCampaignInput(values: CampaignFormValues): CreateCampaignInput {
	const trimmedVeeva = values.veevaCode.trim()
	const selection = classificationSelectionFrom(values)
	return {
		name: values.name.trim(),
		...(values.description.trim().length > 0 ? { description: values.description } : {}),
		...(values.windowLabel.trim().length > 0 ? { windowLabel: values.windowLabel } : {}),
		...(Object.keys(selection).length > 0 ? { classification: selection } : {}),
		...(trimmedVeeva.length > 0 ? { veevaCode: trimmedVeeva } : {}),
	}
}

/**
 * Build the edit diff: only touched fields are included. `description`/
 * `windowLabel` emit an explicit `null` when cleared; `veevaCode` is omitted
 * when cleared (never `null`, matching the real, non-nullable update contract).
 */
export function buildUpdateCampaignInput(
	values: CampaignFormValues,
	touched: CampaignFormTouched,
): UpdateCampaignInput {
	const input: UpdateCampaignInput = {}
	if (touched.name) input.name = values.name.trim()
	if (touched.description) input.description = values.description.trim().length > 0 ? values.description : null
	if (touched.windowLabel) input.windowLabel = values.windowLabel.trim().length > 0 ? values.windowLabel : null
	if (touched.veevaCode) {
		const trimmed = values.veevaCode.trim()
		if (trimmed.length > 0) input.veevaCode = trimmed
	}
	if (touched.objectiveCode || touched.geographyCode || touched.brandCode || touched.productCode) {
		input.classification = classificationSelectionFrom(values)
	}
	return input
}
